import { useCallback, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  publishProcess,
  type ProcessDetailDto,
} from '@api/processes';
import { trackFunnelEvent } from '@utils/datadog';
import { processDetailQueryKey } from './useProcessDetail';
import { processesListRootKey } from './useProcesses';

/**
 * Subset of `useProcessAutoSave`'s return shape that the publish flow needs.
 * Pulling it out as a structural type keeps the hook decoupled from
 * `useProcessAutoSave` (so `useProcessPublish` is testable with a tiny mock
 * instead of pulling the whole auto-save machinery into a unit test).
 */
export interface ProcessPublishAutoSave {
  flush: (overrides?: { name?: string }) => Promise<ProcessDetailDto | undefined>;
}

interface PublishErrorBody {
  error?: string;
  message?: string;
}

export interface UseProcessPublishParams {
  processId: string | undefined;
  detail: ProcessDetailDto | null | undefined;
  autoSave: ProcessPublishAutoSave;
  queryClient: QueryClient;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

export interface UseProcessPublishResult {
  isPublishing: boolean;
  /** Trigger a publish; safe to call when already publishing — it short-circuits. */
  publish: () => Promise<void>;
}

/**
 * Resolve the snackbar message + side-effect for a failed publish. Pulled
 * out of the publish flow so the if/else chain doesn't add to the calling
 * function's cognitive complexity, and so the mapping is unit-testable
 * without spinning up a full QueryClient + auto-save fixture.
 */
const mapPublishError = (
  err: unknown,
): { message: string; shouldInvalidateDetail: boolean } => {
  const ax = err as AxiosError<PublishErrorBody> | undefined;
  const code = ax?.response?.data?.error;
  const fallback =
    ax?.response?.data?.message ??
    (err instanceof Error ? err.message : 'Failed to publish process');
  if (code === 'DRIFT_DETECTED') {
    return {
      message: 'Process changed in another tab. Refreshing...',
      shouldInvalidateDetail: true,
    };
  }
  if (code === 'TEST_RUN_ACTIVE') {
    return {
      message: 'Cancel the active test run before publishing.',
      shouldInvalidateDetail: false,
    };
  }
  if (code === 'VALIDATION_FAILED') {
    return {
      message: 'Fix validation errors before publishing.',
      shouldInvalidateDetail: false,
    };
  }
  return { message: fallback, shouldInvalidateDetail: false };
};

/**
 * Owns the publish flow:
 *
 *   1. Flush any pending auto-save so the {@code expectedUpdatedAt} we
 *      send below is the user's actual latest version.
 *   2. POST /publish with the drift token. Backend re-validates so the
 *      client doesn't have to. Publish is in-place (AIB-6984), so the
 *      response carries the same {@code processId} and we just refresh
 *      the existing detail cache.
 *   3. On success, invalidate detail (now PUBLISHED) and the list family
 *      (gallery row toggles tag).
 *   4. On 409 DRIFT_DETECTED / TEST_RUN_ACTIVE / 422 VALIDATION_FAILED,
 *      surface a snackbar with the actionable message via
 *      {@link mapPublishError}.
 *
 * Lives in its own hook so the cognitive complexity of `ProcessEditorPage`
 * stays bounded — the publish flow's six branches and try/catch/finally
 * are local to this file.
 */
export const useProcessPublish = ({
  processId,
  detail,
  autoSave,
  queryClient,
  showSuccess,
  showError,
}: UseProcessPublishParams): UseProcessPublishResult => {
  const [isPublishing, setIsPublishing] = useState(false);

  const publish = useCallback(async () => {
    if (!processId || !detail || isPublishing) return;
    setIsPublishing(true);
    try {
      const flushed = await autoSave.flush();
      const expectedUpdatedAt = flushed?.updatedAt ?? detail.updatedAt;
      const fresh = await publishProcess(processId, { expectedUpdatedAt });
      queryClient.setQueryData(
        processDetailQueryKey(fresh.processId),
        fresh,
      );
      queryClient
        .invalidateQueries({ queryKey: processesListRootKey })
        .catch(() => undefined);
      trackFunnelEvent('process_published', { processId });
      showSuccess('Process published.');
    } catch (err) {
      const mapped = mapPublishError(err);
      showError(mapped.message);
      if (mapped.shouldInvalidateDetail) {
        queryClient
          .invalidateQueries({ queryKey: processDetailQueryKey(processId) })
          .catch(() => undefined);
      }
    } finally {
      setIsPublishing(false);
    }
  }, [processId, detail, isPublishing, autoSave, queryClient, showSuccess, showError]);

  return { isPublishing, publish };
};
